import { __awaiter, __generator } from "tslib";
import * as _ from 'lodash';
import { WixStoresHttp } from '../../http/http';
import { FetchAdapter } from '../../http/fetch-wrapper';
import { CacheKey, CSRF_HEADER_NAME, MULTI_LANG_HEADER_NAME, Topology, APP_DEFINITION_ID } from '../../constants';
import Experiments from '@wix/wix-experiments';
import { query as getConfigQuery } from '../../graphql/getConfig.graphql';
import { isWorker } from '../utils';
import { createOOIStoreFrontBILogger } from '../../bi/configure-ooi-front-bi-logger';
import { QueryParamsService } from '../../services/QueryParamsService';
var InstanceManager = /** @class */ (function () {
    function InstanceManager(site) {
        this.site = site;
    }
    InstanceManager.prototype.getInstance = function () {
        return this.site.getAppToken(APP_DEFINITION_ID);
    };
    return InstanceManager;
}());
export { InstanceManager };
var SiteStore = /** @class */ (function () {
    function SiteStore(_a, _b, _c, platform) {
        var _d;
        var _this = this;
        var instance = _a.instance, instanceId = _a.instanceId, baseUrls = _a.baseUrls;
        var pubSub = _b.pubSub, storage = _b.storage;
        var wixWindow = _c.window, site = _c.site, location = _c.location, seo = _c.seo, usersApi = _c.user;
        this.pageMap = {
            cart: 'shopping_cart',
            checkout: 'checkout',
            gallery: 'product_gallery',
            thankyou: 'thank_you_page',
            product: 'product_page',
        };
        this.locale = 'en';
        this.instance = instance;
        this.formFactor = wixWindow.formFactor;
        this.isMobileFriendly = wixWindow.isMobileFriendly;
        this.viewMode = wixWindow.viewMode;
        this.pubSub = pubSub;
        this.siteApis = site;
        this.location = location;
        this.seo = seo;
        this.windowApis = wixWindow;
        this.usersApi = usersApi;
        this.storeId = instanceId;
        this.msid = platform.bi.metaSiteId;
        this.platformServices = platform;
        this.storage = storage;
        var user = { visitor_id: platform.bi.visitorId };
        this.uuid = platform.bi.visitorId;
        this.biLogger = createOOIStoreFrontBILogger(user, this.platformServices.bi.biToken, {
            storeId: this.storeId,
            isMerchant: false,
            appName: 'Stores',
            _msid: this.msid,
        }, this.platformServices.biLoggerFactory());
        this.fetchAdapter = new FetchAdapter();
        this.httpClient = new WixStoresHttp(new InstanceManager(site), this.fetchAdapter, function () { return _this.getCurrencyHeader(); });
        this.queryParamsService = new QueryParamsService(this.location);
        var multiLangHeader = this.getMultiLangHeader();
        if (multiLangHeader) {
            this.httpClient.addHeaders((_d = {}, _d[MULTI_LANG_HEADER_NAME] = multiLangHeader, _d));
        }
        this.addCSRFToken();
        if (!this.location.navigateToSection) {
            this.location.navigateToSection = function () { return Promise.resolve(); };
        }
        var overrideBaseUrl = _.has(location.query, 'localDevServer');
        this.baseUrls = _.mapValues(baseUrls, function (url) { return (overrideBaseUrl ? location.query.localDevServer : url); });
        this.isSiteMode() && this.purgeMemoryCache();
        site.onInstanceChanged(function (event) {
            _this.instance = event.instance;
        });
    }
    SiteStore.prototype.loadClientConfig = function () {
        var _this = this;
        var cache = this.storage.memory.getItem(CacheKey.GET_CONFIG);
        if (cache) {
            var cachedData = JSON.parse(cache);
            if (cachedData.clientConfig) {
                this.handleGetConfigData(cachedData);
                return Promise.resolve();
            }
        }
        return this.httpClient
            .post(this.resolveAbsoluteUrl("/" + Topology.STOREFRONT_GRAPHQL_URL), {
            query: getConfigQuery,
            source: 'WixStoresWebClient',
            operationName: 'getConfig',
        })
            .then(function (_a) {
            var data = _a.data;
            _this.isSiteMode() && _this.storage.memory.setItem(CacheKey.GET_CONFIG, JSON.stringify(data));
            _this.handleGetConfigData(data);
        });
    };
    SiteStore.prototype.isMobile = function () {
        if (!this.isMobileFriendly) {
            return false;
        }
        return this.formFactor === 'Mobile';
    };
    SiteStore.prototype.isDesktop = function () {
        return this.formFactor === 'Desktop';
    };
    SiteStore.prototype.isTablet = function () {
        return this.formFactor === 'Tablet';
    };
    SiteStore.prototype.isEditorMode = function () {
        return this.viewMode === 'Editor';
    };
    SiteStore.prototype.isSiteMode = function () {
        return this.viewMode === 'Site';
    };
    SiteStore.prototype.isPreviewMode = function () {
        return this.viewMode === 'Preview';
    };
    SiteStore.prototype.getSectionUrl = function (sectionId) {
        return this.siteApis.getSectionUrl({ sectionId: sectionId });
    };
    SiteStore.prototype.getOrigin = function () {
        var baseUrl = this.location.baseUrl;
        var baseUrlParts = baseUrl.split('/');
        return baseUrlParts.slice(0, 3).join('/');
    };
    SiteStore.prototype.resolveAbsoluteUrl = function (path) {
        return this.isSSR() ? "" + this.getOrigin() + path : path;
    };
    SiteStore.prototype.isInteractive = function () {
        return this.windowApis.rendering.env === 'browser';
    };
    SiteStore.prototype.isSSR = function () {
        return this.windowApis.rendering.env === 'backend';
    };
    SiteStore.prototype.isRTL = function () {
        return this.layoutDirection === 'rtl';
    };
    SiteStore.prototype.navigate = function (conf, forceNavigate) {
        if (forceNavigate === void 0) { forceNavigate = false; }
        return __awaiter(this, void 0, void 0, function () {
            var relativeUrl;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!isWorker()) return [3 /*break*/, 2];
                        if (!forceNavigate) {
                            return [2 /*return*/, Promise.resolve()];
                        }
                        return [4 /*yield*/, this.getSectionUrl(conf.sectionId)];
                    case 1:
                        relativeUrl = (_a.sent()).relativeUrl;
                        this.location.to(conf.state ? relativeUrl + "/" + conf.state : relativeUrl);
                        _a.label = 2;
                    case 2:
                        if (!isWorker()) {
                            this.location.navigateToSection(conf); // tslint:disable-line no-floating-promises
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    SiteStore.prototype.navigateToLink = function (link) {
        this.location.to(isWorker() ? link.url : link.sdkLink);
    };
    SiteStore.prototype.getHomepageLink = function () {
        return __awaiter(this, void 0, void 0, function () {
            var siteMap, homepage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.siteApis.getSiteStructure()];
                    case 1:
                        siteMap = _a.sent();
                        homepage = siteMap.pages.find(function (p) { return p.isHomePage; }) || siteMap.pages[0];
                        return [2 /*return*/, {
                                sdkLink: { pageId: homepage.id, type: 'PageLink' },
                                url: "" + this.location.baseUrl + homepage.url,
                            }];
                }
            });
        });
    };
    SiteStore.prototype.getMultiLangFields = function () {
        var currentShortLang = this.windowApis.multilingual.currentLanguage;
        var currentLang = this.windowApis.multilingual.siteLanguages.filter(function (lang) { return lang.languageCode === currentShortLang; });
        if (currentLang.length > 0) {
            return {
                isPrimaryLanguage: currentLang[0].isPrimaryLanguage,
                lang: currentShortLang,
                locale: currentLang[0].locale,
            };
        }
        return null;
    };
    SiteStore.prototype.getCurrencyFields = function () {
        var currentCurrency = this.queryParamsService.getQueryParam('currency');
        if (currentCurrency) {
            return {
                currencyCode: currentCurrency,
            };
        }
        return null;
    };
    SiteStore.prototype.getMultiLangHeader = function () {
        var fields = this.getMultiLangFields();
        return fields ? fields.lang + "|" + fields.locale + "|" + fields.isPrimaryLanguage.toString() + "|" + this.storeId : '';
    };
    SiteStore.prototype.getCurrencyHeader = function () {
        var fields = this.getCurrencyFields();
        return fields ? "" + fields.currencyCode : '';
    };
    SiteStore.prototype.addCSRFToken = function () {
        var _a;
        var csrf = this.platformServices.getCsrfToken();
        if (csrf) {
            this.httpClient.addHeaders((_a = {}, _a[CSRF_HEADER_NAME] = csrf, _a));
        }
    };
    SiteStore.prototype.handleGetConfigData = function (data) {
        this.handleExperiments(data.experiments);
        this.locale = data.clientConfig.language;
        this.currency = data.clientConfig.storeCurrency;
        this.layoutDirection = data.clientConfig.layoutDirection;
    };
    SiteStore.prototype.handleExperiments = function (experimentsData) {
        var experiments = (experimentsData || []).reduce(function (acc, e) {
            acc[e.name] = e.value;
            return acc;
        }, {});
        this.experiments = new Experiments({ experiments: experiments });
    };
    SiteStore.prototype.purgeMemoryCache = function () {
        var cachedLang = this.storage.memory.getItem(CacheKey.MULTI_LANG);
        if (cachedLang !== this.windowApis.multilingual.currentLanguage) {
            this.storage.memory.clear();
            this.storage.memory.setItem(CacheKey.MULTI_LANG, this.windowApis.multilingual.currentLanguage);
        }
    };
    return SiteStore;
}());
export { SiteStore };
//# sourceMappingURL=siteStore.js.map